import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import EditorialMarquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { IMG, CATEGORIES, ESSENTIALS } from "@/data/content";

const BEDDING = ["Bed Sheets", "Bed Runners", "Duvets", "Duvet Covers", "Pillows", "Pillow Covers", "Mattresses", "Mattress Protectors"];
const BATH = ["Bath Towels", "Pool Towels", "Hand Towels", "Face Towels", "Bath Mats", "Bath Robes"];

const ItemChips = ({ items, dark = false }) => (
  <ul className="flex flex-wrap gap-3 mt-8">
    {items.map((item) => (
      <li
        key={item}
        data-testid={`product-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`text-[12px] tracking-[0.14em] uppercase px-4 py-2 border transition-colors duration-300 ${
          dark
            ? "border-ivory/25 text-ivory/80 hover:border-gold hover:text-gold"
            : "border-navy/20 text-navy/75 hover:border-gold hover:text-gold"
        }`}
      >
        {item}
      </li>
    ))}
  </ul>
);

const CollectionBlock = ({ number, eyebrow, title, text, items, image, reverse = false, dark = false }) => (
  <section className={`py-24 md:py-32 ${dark ? "bg-navy" : ""}`}>
    <div className={`max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center`}>
      <div className={reverse ? "lg:order-2" : ""}>
        <SectionHeader number={number} eyebrow={eyebrow} title={title} dark={dark} />
        <Reveal delay={0.15}>
          <p className={`mt-6 text-base md:text-lg font-light leading-relaxed ${dark ? "text-ivory/70" : "text-navy/70"}`}>
            {text}
          </p>
          <ItemChips items={items} dark={dark} />
        </Reveal>
      </div>
      <Reveal delay={0.2} className={`relative ${reverse ? "lg:order-1" : ""}`}>
        <div className={`absolute w-full h-full border pointer-events-none ${
          reverse ? "-bottom-4 -left-4" : "-top-4 -right-4"
        } ${dark ? "border-gold/40" : "border-gold/50"}`} />
        <div className="overflow-hidden group">
          <img
            src={image}
            alt={title}
            loading="lazy"
            className="w-full h-[420px] md:h-[540px] object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const Products = () => (
  <main data-testid="products-page" className="relative">
    <Link
      to="/"
      data-testid="products-back-home-btn"
      className="absolute top-24 md:top-28 left-6 md:left-12 z-20 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-ivory/80 hover:text-gold transition-colors duration-300 border border-ivory/30 hover:border-gold px-4 py-2.5"
    >
      <ArrowLeft size={14} />
      Back to Home
    </Link>
    <PageHero
      eyebrow="Our Collections"
      title="Hotel Linen & Hospitality"
      italic="Products"
      description="Hotel bed sheets, bed linen, towels, duvet covers, pillows, mattresses and hospitality textiles — designed for hospitality businesses and supplied across India."
      image={IMG.hotelRoom2}
    />

    <CollectionBlock
      number="01"
      eyebrow="Bedding Collection"
      title="Premium Bedding for Exceptional Hospitality"
      text="Designed for comfort, presentation and everyday hospitality performance."
      items={BEDDING}
      image={IMG.bedroom2}
    />

    <CollectionBlock
      number="02"
      eyebrow="Bath Collection"
      title="Comfort Beyond the Room"
      text="Complete your hospitality experience with dependable bath textiles designed for comfort, presentation and practical use."
      items={BATH}
      image={IMG.bathrobe}
      reverse
      dark
    />

    <section className="py-24 md:py-32 bg-linen/40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <SectionHeader
          number="03"
          eyebrow="More Collections"
          title="Hospitality Textiles & Room Essentials"
          description="Table napkins and hospitality linen for dining spaces, plus cushions, cushion covers and fleece blankets that add warmth to every room."
        />
        <div className="grid md:grid-cols-2 gap-6 mt-16">
          {CATEGORIES.filter((c) => ["hospitality", "comfort"].includes(c.id)).map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1}>
              <div data-testid={`collection-card-${c.id}`} className="group bg-ivory border border-navy/10 h-full">
                <div className="overflow-hidden h-72">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl font-medium text-navy">{c.title}</h3>
                  <ItemChips items={c.items} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <SectionHeader
          number="04"
          eyebrow="The Full Range"
          title="Everything You Need for Hospitality"
          description="For specifications, bulk requirements and quotations, contact our team — we will help you plan the right products for your property."
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-navy/10 border border-navy/10 mt-16">
          {ESSENTIALS.map((e, i) => (
            <Reveal key={e} delay={i * 0.04}>
              <Link
                to="/quote"
                data-testid={`essential-${e.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex flex-col items-start justify-between bg-ivory p-7 h-36 transition-colors duration-500 hover:bg-navy"
              >
                <span className="text-[10px] tracking-[0.28em] text-gold">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-lg md:text-xl text-navy group-hover:text-ivory transition-colors duration-500">
                  {e}
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <EditorialMarquee />
    <CTASection
      title="Looking for Hospitality Textiles in Bulk?"
      text="Whether you are furnishing a new property or upgrading an existing one, talk to our team about your requirements."
      image={IMG.hotelRoom}
    />
  </main>
);

export default Products;
